/**
 * Webtech HR Engine™ - Assistant d'Onboarding Obligatoire
 * Guide étape par étape pour Admin RH, Manager et Employé
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePlan } from '../../contexts/PlanContext';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  CheckCircle2,
  Circle,
  ChevronRight,
  Users,
  Building2,
  Target,
  ClipboardCheck,
  Sparkles,
  FileText,
  Award,
  TrendingUp,
  UserCircle,
  Briefcase,
  Star,
  ArrowRight,
  X
} from 'lucide-react';
import { toast } from 'sonner';

// Onboarding steps per role
const ONBOARDING_STEPS = {
  Admin: [
    {
      id: 'create_departments',
      title: 'Créer vos départements',
      description: 'Organisez votre entreprise en départements',
      icon: Building2,
      path: '/employees',
      action: 'Ajouter un département',
      color: 'bg-blue-100 text-blue-700'
    },
    {
      id: 'add_employees',
      title: 'Ajouter vos employés',
      description: 'Importez ou créez votre équipe',
      icon: Users,
      path: '/employees/new',
      action: 'Ajouter un employé',
      color: 'bg-green-100 text-green-700'
    },
    {
      id: 'define_objectives',
      title: 'Définir les objectifs globaux',
      description: 'Créez les objectifs entreprise et département',
      icon: Target,
      path: '/objectives',
      action: 'Créer un objectif',
      color: 'bg-purple-100 text-purple-700',
      isPro: true
    },
    {
      id: 'launch_campaign',
      title: 'Lancer une campagne d\'évaluation',
      description: 'Configurez votre première campagne',
      icon: ClipboardCheck,
      path: '/performance/campaigns',
      action: 'Créer une campagne',
      color: 'bg-amber-100 text-amber-700',
      isPro: true
    },
    {
      id: 'activate_ai',
      title: 'Activer l\'analyse IA',
      description: 'Débloquez les recommandations intelligentes',
      icon: Sparkles,
      path: '/ai-insights',
      action: 'Découvrir l\'IA',
      color: 'bg-pink-100 text-pink-700',
      isPro: true
    }
  ],
  Manager: [
    {
      id: 'review_team',
      title: 'Découvrir votre équipe',
      description: 'Visualisez les membres de votre équipe',
      icon: Users,
      path: '/manager-dashboard',
      action: 'Voir mon équipe',
      color: 'bg-blue-100 text-blue-700'
    },
    {
      id: 'create_team_objectives',
      title: 'Définir les objectifs d\'équipe',
      description: 'Créez des objectifs pour votre équipe',
      icon: Target,
      path: '/objectives',
      action: 'Créer un objectif',
      color: 'bg-green-100 text-green-700',
      isPro: true
    },
    {
      id: 'assign_individual_objectives',
      title: 'Assigner des objectifs individuels',
      description: 'Personnalisez les objectifs pour chaque membre',
      icon: UserCircle,
      path: '/objectives',
      action: 'Assigner',
      color: 'bg-purple-100 text-purple-700',
      isPro: true
    },
    {
      id: 'validate_proposals',
      title: 'Valider les propositions',
      description: 'Approuvez les objectifs proposés par votre équipe',
      icon: CheckCircle2,
      path: '/objectives',
      action: 'Voir les propositions',
      color: 'bg-amber-100 text-amber-700',
      isPro: true
    }
  ],
  'Employé': [
    {
      id: 'complete_profile',
      title: 'Compléter votre profil',
      description: 'Ajoutez vos informations et compétences',
      icon: UserCircle,
      path: '/profile',
      action: 'Modifier mon profil',
      color: 'bg-blue-100 text-blue-700'
    },
    {
      id: 'view_objectives',
      title: 'Consulter vos objectifs',
      description: 'Découvrez les objectifs qui vous sont assignés',
      icon: Target,
      path: '/my-evaluations',
      action: 'Voir mes objectifs',
      color: 'bg-green-100 text-green-700',
      isPro: true
    },
    {
      id: 'propose_objectives',
      title: 'Proposer des objectifs',
      description: 'Suggérez vos propres objectifs à votre manager',
      icon: Star,
      path: '/objectives',
      action: 'Proposer',
      color: 'bg-purple-100 text-purple-700',
      isPro: true
    },
    {
      id: 'self_evaluate',
      title: 'Auto-évaluation',
      description: 'Évaluez votre progression sur vos objectifs',
      icon: ClipboardCheck,
      path: '/my-evaluations',
      action: 'M\'auto-évaluer',
      color: 'bg-amber-100 text-amber-700',
      isPro: true
    }
  ]
};

export default function OnboardingWizard({ isOpen, onClose, forceShow = false }) {
  const { user } = useAuth();
  const { isPro } = usePlan();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const role = user?.role || 'Employé';
  const steps = ONBOARDING_STEPS[role] || ONBOARDING_STEPS['Employé'];
  
  // Load completed steps from backend
  useEffect(() => {
    const loadProgress = async () => {
      try {
        const response = await api.get('/onboarding/progress');
        setCompletedSteps(response.data?.completedSteps || []);
      } catch (error) {
        // If endpoint doesn't exist, start fresh
        setCompletedSteps([]);
      } finally {
        setLoading(false);
      }
    };
    
    if (isOpen) {
      loadProgress();
    }
  }, [isOpen]);
  
  const markStepComplete = async (stepId) => {
    if (completedSteps.includes(stepId)) return;
    
    const newCompleted = [...completedSteps, stepId];
    setCompletedSteps(newCompleted);
    
    try {
      await api.post('/onboarding/progress', { stepId, completed: true });
    } catch (error) {
      // Silent fail - progress still saved locally
    }
    
    // Move to next step
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const handleStepAction = (step) => {
    if (step.isPro && !isPro) {
      toast.info('🔒 Cette fonctionnalité nécessite le plan PRO', {
        action: {
          label: 'Découvrir PRO',
          onClick: () => navigate('/billing/subscription')
        }
      });
      return;
    }
    
    markStepComplete(step.id);
    navigate(step.path);
    onClose();
  };
  
  const progress = (completedSteps.length / steps.length) * 100;
  const allCompleted = completedSteps.length === steps.length;
  
  const handleSkip = async () => {
    try {
      await api.post('/onboarding/skip');
      // Mémoriser aussi dans localStorage pour éviter réapparition sur refresh
      localStorage.setItem('hr_onboarding_wizard_dismissed', new Date().toISOString());
    } catch (error) {
      // Silent fail - but still save to localStorage
      localStorage.setItem('hr_onboarding_wizard_dismissed', new Date().toISOString());
    }
    onClose();
  };
  
  if (loading) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl flex items-center gap-2">
                <Award className="w-6 h-6 text-blue-600" />
                Bienvenue sur HR Engine™
              </DialogTitle>
              <DialogDescription className="mt-1">
                {role === 'Admin' && 'Configurez votre espace RH en quelques étapes'}
                {role === 'Manager' && 'Prenez en main la gestion de votre équipe'}
                {role === 'Employé' && 'Découvrez votre espace personnel'}
              </DialogDescription>
            </div>
            <Badge className={allCompleted ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
              {completedSteps.length}/{steps.length} étapes
            </Badge>
          </div>
        </DialogHeader>
        
        {/* Progress bar */}
        <div className="mt-4">
          <Progress value={progress} className="h-2" />
          <p className="text-xs text-slate-500 mt-1 text-right">{Math.round(progress)}% complété</p>
        </div>
        
        {/* Steps list */}
        <div className="mt-6 space-y-3">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isCompleted = completedSteps.includes(step.id);
            const isCurrent = index === currentStep && !isCompleted;
            const isLocked = step.isPro && !isPro;
            
            return (
              <Card 
                key={step.id}
                className={`transition-all cursor-pointer hover:shadow-md ${
                  isCurrent ? 'ring-2 ring-blue-500 bg-blue-50/50' : ''
                } ${isCompleted ? 'bg-green-50/50' : ''} ${isLocked ? 'opacity-60' : ''}`}
                onClick={() => !isLocked && handleStepAction(step)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Step number/status */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isCompleted ? 'bg-green-100' : step.color
                    }`}>
                      {isCompleted ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <Icon className="w-5 h-5" />
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className={`font-semibold ${
                          isCompleted ? 'text-green-700 line-through' : 'text-slate-900'
                        }`}>
                          {step.title}
                        </h4>
                        {step.isPro && (
                          <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                            PRO
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">{step.description}</p>
                    </div>
                    
                    {/* Action */}
                    <Button 
                      size="sm" 
                      variant={isCompleted ? 'ghost' : 'default'}
                      className={isCompleted ? 'text-green-600' : ''}
                      disabled={isLocked}
                    >
                      {isCompleted ? 'Fait' : step.action}
                      {!isCompleted && <ChevronRight className="w-4 h-4 ml-1" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        
        {/* Completion message */}
        {allCompleted && (
          <Card className="mt-4 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Award className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-green-800">Félicitations !</h4>
                <p className="text-sm text-green-700">Vous avez terminé la configuration initiale. Explorez maintenant toutes les fonctionnalités.</p>
              </div>
            </CardContent>
          </Card>
        )}
        
        <DialogFooter className="mt-6">
          <Button variant="ghost" onClick={handleSkip}>
            {allCompleted ? 'Fermer' : 'Passer pour l\'instant'}
          </Button>
          {!allCompleted && (
            <Button onClick={() => handleStepAction(steps[currentStep])}>
              Continuer
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { ONBOARDING_STEPS };
